import {
  Dialog,
  Grid,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { FloatingActionButton, colors } from 'design-system';
import React, { useCallback } from 'react';
import Webcam from 'react-webcam';

// Components (Icons)
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import ReplayIcon from '@material-ui/icons/Replay';

import { cameraOnlyFormat, maxQuality } from './CapturerPreferences';

// Hooks
import useWindowDimensions from './useWindowDimensions';

type Props = {
  onImageCapture: (identifier: string, base64Image: string) => void;
  onCaptureCancelled: () => void;
  imageIdentifier: string;
};

type UseStylesProps = {
  usedWindowDimension: number;
};
const useStyles = makeStyles({
  iconButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    color: colors.white,
    zIndex: 10,
  },
  containerGrid: {
    width: '100%',
    height: '100%',
  },
  whiteText: {
    color: colors.white,
  },
  imagePreview: ({ usedWindowDimension }: UseStylesProps) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: `-${usedWindowDimension / 2}px`,
    marginLeft: `-${usedWindowDimension / 2}px`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    width: usedWindowDimension,
    height: usedWindowDimension,
  }),
});

/**
 * A component used to display a camera layout.
 */
const PhotoCapturer = ({
  onImageCapture,
  onCaptureCancelled,
  imageIdentifier,
}: Props): JSX.Element => {
  const webcamRef = React.useRef<Webcam & HTMLVideoElement>(null);
  const [base64Image, updateBase64Image] = React.useState('');
  const [mediaHasError, updateMediaHasError] = React.useState(false);

  const { width, height } = useWindowDimensions();
  const usedWindowDimension = Math.min(width, height);
  const classes = useStyles({ usedWindowDimension });

  /**
   * This is the method that extracts the screenshot from the camera viewport.
   */
  const capture = useCallback(() => {
    if (webcamRef && webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        updateBase64Image(imageSrc);
      }
    }
  }, [webcamRef]);

  /**
   * Something went wrong, display an error. More often than not, the error stems from
   * the user not giving the page camera permissions.
   */
  function onUserMediaError(): void {
    updateMediaHasError(true);
  }

  /**
   * The "accept" action was invoked after taking the image.
   */
  function handleConfirm(): void {
    onImageCapture(imageIdentifier, base64Image);
  }

  /**
   * The "retake" action is invoked, just clear the current image reference and the
   * UI should adjust.
   */
  function handleRetake(): void {
    updateBase64Image('');
  }

  return (
    <Dialog
      fullScreen
      open
      PaperProps={{
        style: {
          backgroundColor: 'black',
        },
      }}
    >
      <IconButton
        id="sales-web-inspection-button-cancelbutton"
        data-testid="cancel-button"
        onClick={onCaptureCancelled}
        className={classes.iconButton}
      >
        <CloseIcon />
      </IconButton>
      {mediaHasError && (
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          className={classes.containerGrid}
        >
          <Typography
            data-testid="error-text"
            variant="body2"
            className={classes.whiteText}
          >
            No es posible acceder a la cámara...
          </Typography>
        </Grid>
      )}
      {base64Image !== '' && (
        <>
          <div
            data-testid="image-preview"
            className={classes.imagePreview}
            style={{
              backgroundImage: `url(${base64Image})`,
            }}
          />
          <FloatingActionButton
            id="sales-web-inspection-button-bottomleft"
            data-testid="retake-button"
            anchor="bottomLeft"
            buttonContent={<ReplayIcon />}
            onClick={handleRetake}
          />
          <FloatingActionButton
            id="sales-web-inspection-button-bottomright"
            data-testid="accept-button"
            anchor="bottomRight"
            buttonContent={<CheckIcon />}
            onClick={handleConfirm}
          />
        </>
      )}
      {!base64Image && !mediaHasError && (
        <>
          <Webcam
            data-testid="webcam"
            audio={false}
            ref={webcamRef}
            screenshotFormat={cameraOnlyFormat}
            forceScreenshotSourceSize
            videoConstraints={{ facingMode: 'environment' }}
            screenshotQuality={maxQuality / 100}
            onUserMediaError={onUserMediaError}
            width={width}
            height={height}
          />
          <FloatingActionButton
            id="sales-web-inspection-button-takephoto"
            data-testid="take-photo-button"
            anchor="bottomCenter"
            buttonContent={<PhotoCameraIcon />}
            onClick={capture}
          />
        </>
      )}
    </Dialog>
  );
};

export default PhotoCapturer;
